/* eslint-disable react-refresh/only-export-components -- deliberately a mixed module: shared tokens/helpers exported beside components; HMR full-reload is acceptable here. */
import {
    getPayables,
    type PayableItem,
    type PayablesView,
    type PaymentTrancheStatus,
} from '@api/mother-order';
import { getShipmentDetail } from '@api/shipments';
import PageHeader from '@components/common/PageHeader';
import {
    DataSheet,
    type DataSheetColumn,
    type DataSheetFilterConfig,
    type FilterValue,
} from '@components/ui/DataSheet';
import { DatePopover, type UiDateRange } from '@components/ui/DateRange';
import StatCard from '@components/ui/StatCard';
import { Group, Switch, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';

import {
    MarkPaidModal,
    type MarkPaidTarget,
} from '../orders/PaymentScheduleSection';
import ShipmentDetailPanel from '../shipments/ShipmentDetailPanel';
import {
    formatDateDisplay,
    formatMoney,
    StatusBadge,
    textColors,
} from '../procurementOrders/shared';

// Headline cards show ONE currency (USD — the sheet's own planning currency);
// INR folds in at a fixed display rate. Row amounts stay native: that's what
// actually gets wired.
const INR_PER_USD = 96;

const currencyTotalsText = (totals: Record<string, number>): string =>
    Object.entries(totals)
        .filter(([, amt]) => amt > 0.005)
        .map(([ccy, amt]) => formatMoney(amt, ccy))
        .join(' + ') || '—';

const usdValue = (totals: Record<string, number>): string => {
    let usd = 0;
    for (const [ccy, amt] of Object.entries(totals)) {
        if (amt <= 0.005) continue;
        usd += ccy === 'INR' ? amt / INR_PER_USD : amt;
    }
    if (usd <= 0.005) return '—';
    return formatMoney(usd, 'USD');
};

const hasInr = (totals: Record<string, number>): boolean =>
    (totals.INR ?? 0) > 0.005;

/** The event a payment is anchored on, as it reads after "at" or "after". */
const ANCHOR_NOUN: Record<string, string> = {
    ORDER_DATE: 'order',
    READINESS: 'goods ready',
    DISPATCH: 'dispatch',
    ARRIVAL: 'arrival',
    DELIVERY: 'delivery',
    FIXED: 'fixed date',
};

/**
 * One line of terms under a payment's label: "80% · 30d after dispatch", "20% · at order",
 * "80% · 90d after dispatch · +4.7%" for a credit payment (its premium is already in the
 * amount), "custom amount · at delivery" when the figure was set by hand. Empty for rows
 * from a backend that predates these fields, so nothing prints half a sentence.
 */
const describeTerms = (item: PayableItem): string => {
    if (!item.anchor) return '';
    const share = item.percentage == null ? 'custom amount' : `${item.percentage}%`;
    const noun = ANCHOR_NOUN[item.anchor] ?? item.anchor.toLowerCase();
    const days = item.creditDays ?? item.offsetDays ?? 0;
    const when =
        item.anchor === 'FIXED'
            ? 'fixed date'
            : days > 0
              ? `${days}d after ${noun}`
              : `at ${noun}`;
    const premium = item.premiumPct ? ` · +${item.premiumPct}%` : '';
    return `${share} · ${when}${premium}`;
};

const payableToTarget = (item: PayableItem): MarkPaidTarget => ({
    level: item.level,
    orderId: item.orderId,
    trancheId: item.trancheId,
    label: `${item.label} — ${item.orderRef}`,
    amount: item.amount,
    paidAmount: item.paidAmount,
    outstanding: item.outstanding,
    payeeName: item.payeeKind === 'VENDOR' ? null : item.payeeName,
});

// ─── Cross-list chip summary (Orders / Shipments lists reuse this) ──

export type PayableChipSummary = {
    outstandingByCurrency: Record<string, number>;
    worstStatus: PaymentTrancheStatus;
};

const STATUS_SEVERITY: PaymentTrancheStatus[] = [
    'OVERDUE',
    'DUE_SOON',
    'DUE',
    'PARTIALLY_PAID',
    'SCHEDULED',
    'AWAITING_ANCHOR',
    'PAID',
    'CANCELLED',
];

/**
 * Index the payables view for list chips: outstanding + worst status per
 * mother order and per shipment/standalone PO. Orders/shipments with no
 * OPEN obligations simply have no entry (the chip stays blank).
 */
export function summarizePayables(view: PayablesView | undefined): {
    byMotherId: Map<string, PayableChipSummary>;
    byPoId: Map<string, PayableChipSummary>;
} {
    const byMotherId = new Map<string, PayableChipSummary>();
    const byPoId = new Map<string, PayableChipSummary>();
    if (!view) return { byMotherId, byPoId };

    const fold = (
        map: Map<string, PayableChipSummary>,
        key: string,
        item: PayableItem,
    ) => {
        const entry = map.get(key) ?? {
            outstandingByCurrency: {},
            worstStatus: item.status,
        };
        entry.outstandingByCurrency[item.currency] =
            (entry.outstandingByCurrency[item.currency] ?? 0) +
            Math.max(0, item.outstanding);
        if (
            STATUS_SEVERITY.indexOf(item.status) <
            STATUS_SEVERITY.indexOf(entry.worstStatus)
        ) {
            entry.worstStatus = item.status;
        }
        map.set(key, entry);
    };

    for (const item of [
        ...view.overdue,
        ...view.unscheduled,
        ...view.weeks.flatMap((w) => w.items),
    ]) {
        if (item.level === 'MOTHER') fold(byMotherId, item.orderId, item);
        else {
            fold(byPoId, item.orderId, item);
            if (item.motherOrderId) fold(byMotherId, item.motherOrderId, item);
        }
    }
    return { byMotherId, byPoId };
}

export const PaymentChip: FC<{ summary: PayableChipSummary | undefined }> = ({
    summary,
}) => {
    if (!summary) {
        return <span style={{ color: textColors.muted, fontSize: 12 }}>—</span>;
    }
    return (
        <Group gap={6} wrap="nowrap">
            <StatusBadge status={summary.worstStatus} />
            <span style={{ fontSize: 12, color: textColors.secondary }}>
                {currencyTotalsText(summary.outstandingByCurrency)}
            </span>
        </Group>
    );
};

// ─── The Payments tab ───────────────────────────────────────────

const SectionTitle: FC<{ title: string; sub?: string; color?: string }> = ({
    title,
    sub,
    color,
}) => (
    <Group gap={10} mt={20} mb={8}>
        <Text
            size="12px"
            fw={700}
            c={color ?? textColors.primary}
            style={{ letterSpacing: '0.05em' }}
        >
            {title.toUpperCase()}
        </Text>
        {sub && (
            <Text size="12px" c={textColors.muted}>
                {sub}
            </Text>
        )}
    </Group>
);

const STATUS_RANK: Record<string, number> = {
    OVERDUE: 0,
    DUE_SOON: 1,
    DUE: 2,
    PARTIALLY_PAID: 3,
    SCHEDULED: 4,
    AWAITING_ANCHOR: 5,
};

const weekStartIso = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    return d.toISOString().slice(0, 10);
};

const addDaysIso = (iso: string, days: number): string => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};

const PaymentsContainer: FC = () => {
    const queryClient = useQueryClient();
    const [includeForecast, setIncludeForecast] = useState(true);
    const [markPaid, setMarkPaid] = useState<PayableItem | null>(null);
    const [vendorFilterValue, setVendorFilterValue] = useState<FilterValue>({
        vendor: [],
    });
    const [dueFrom, setDueFrom] = useState('');
    const [dueTo, setDueTo] = useState('');
    const [showDueCalendar, setShowDueCalendar] = useState(false);
    const [selectedPoId, setSelectedPoId] = useState<string | null>(null);
    const selectedVendors = vendorFilterValue.vendor ?? [];

    const { data: view, isLoading } = useQuery(
        ['payables', includeForecast],
        () => getPayables(includeForecast),
    );

    const refresh = () => void queryClient.invalidateQueries(['payables']);

    // The shipment side panel (shared with the Shipments tab / order detail).
    // Mother-level rows (the advance on authored orders) have no shipment to
    // open; shipment/standalone rows fetch their detail on click.
    const { data: selectedShipment } = useQuery(
        ['shipment-detail', selectedPoId],
        () => getShipmentDetail(selectedPoId!),
        { enabled: !!selectedPoId },
    );

    const openItems = useMemo(() => {
        if (!view) return [];
        return [
            ...view.overdue,
            ...view.weeks.flatMap((w) => w.items),
            ...view.unscheduled,
        ].sort((a, b) => {
            const r = (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
            if (r !== 0) return r;
            if (!a.dueDate) return 1;
            if (!b.dueDate) return -1;
            return a.dueDate < b.dueDate ? -1 : 1;
        });
    }, [view]);

    const vendorFilterConfig = useMemo<DataSheetFilterConfig>(() => {
        const options = [
            ...new Set(openItems.map((i) => i.vendorName).filter(Boolean)),
        ]
            .sort()
            .map((v) => ({ label: v!, value: v! }));
        return {
            label: 'Vendor',
            params: [
                {
                    key: 'vendor',
                    label: 'Vendor',
                    mode: 'multi',
                    options,
                    searchPlaceholder: 'Search vendors...',
                },
            ],
            value: vendorFilterValue,
            onChange: setVendorFilterValue,
        };
    }, [openItems, vendorFilterValue]);

    const filteredItems = useMemo(
        () =>
            openItems.filter((i) => {
                if (
                    selectedVendors.length > 0 &&
                    (!i.vendorName || !selectedVendors.includes(i.vendorName))
                ) {
                    return false;
                }
                if (dueFrom || dueTo) {
                    if (!i.dueDate) return false;
                    if (dueFrom && i.dueDate < dueFrom) return false;
                    if (dueTo && i.dueDate > dueTo) return false;
                }
                return true;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openItems, vendorFilterValue, dueFrom, dueTo],
    );

    const hasFilters = selectedVendors.length > 0 || !!dueFrom || !!dueTo;

    // Card totals follow the filters (computed client-side from the rows).
    const cardTotals = useMemo(() => {
        const add = (t: Record<string, number>, i: PayableItem) => {
            t[i.currency] = (t[i.currency] ?? 0) + Math.max(0, i.outstanding);
        };
        const today = view?.today ?? new Date().toISOString().slice(0, 10);
        const plus30 = addDaysIso(today, 30);
        const thisWeek = weekStartIso(today);

        const totals = {
            overdue: {} as Record<string, number>,
            dueThisWeek: {} as Record<string, number>,
            next30: {} as Record<string, number>,
            unscheduled: {} as Record<string, number>,
        };
        let overdueCount = 0;
        let unscheduledCount = 0;
        for (const i of filteredItems) {
            const stale =
                i.dueDateSource === 'EXPECTED' && !!i.dueDate && i.dueDate < today;
            if (i.status === 'OVERDUE') {
                add(totals.overdue, i);
                overdueCount++;
            } else if (!i.dueDate || stale) {
                add(totals.unscheduled, i);
                unscheduledCount++;
            } else {
                if (weekStartIso(i.dueDate) === thisWeek) add(totals.dueThisWeek, i);
                if (i.dueDate <= plus30) add(totals.next30, i);
            }
        }
        return { ...totals, overdueCount, unscheduledCount };
    }, [filteredItems, view]);

    // Page-level alert: payments falling due within 5 days, on REAL dates
    // only (pinned/actual — projections never alarm).
    const dueSoonAlert = useMemo(() => {
        if (!view) return null;
        const today = view.today;
        const plus5 = addDaysIso(today, 5);
        const items = openItems.filter(
            (i) =>
                !!i.dueDate &&
                i.dueDateSource !== 'EXPECTED' &&
                i.dueDate >= today &&
                i.dueDate <= plus5,
        );
        if (items.length === 0) return null;
        const totals: Record<string, number> = {};
        for (const i of items) {
            totals[i.currency] =
                (totals[i.currency] ?? 0) + Math.max(0, i.outstanding);
        }
        return { count: items.length, amount: usdValue(totals) };
    }, [openItems, view]);

    const columns: DataSheetColumn<PayableItem>[] = useMemo(
        () => [
            {
                key: 'orderRef',
                header: 'Order / Shipment',
                width: 180,
                sticky: true,
                render: (_: unknown, r: PayableItem) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>
                        {r.orderRef}
                        {r.motherOrderNumber && r.motherOrderNumber !== r.orderRef && (
                            <span style={{ color: textColors.muted, fontSize: 11 }}>
                                {' '}
                                · {r.motherOrderNumber}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                key: 'vendorName',
                header: 'Vendor',
                width: 140,
                render: (v: string | null) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {v ?? '—'}
                    </span>
                ),
            },
            {
                key: 'label',
                header: 'Payment',
                width: 150,
                // The terms beneath the label: "Balance" is the name of a payment and
                // "80% · 30d after dispatch" is what the name means on this row.
                render: (v: string, r: PayableItem) => (
                    <span style={{ fontSize: 12, color: textColors.primary, lineHeight: 1.25 }}>
                        {v}
                        {describeTerms(r) && (
                            <span style={{ display: 'block', fontSize: 11, color: textColors.muted, whiteSpace: 'nowrap' }}>
                                {describeTerms(r)}
                            </span>
                        )}
                    </span>
                ),
            },
            {
                key: 'payeeName',
                header: 'Pay to',
                width: 110,
                render: (_: unknown, r: PayableItem) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {r.payeeKind === 'CREDIT_PARTNER'
                            ? (r.payeeName ?? 'Credit partner')
                            : r.payeeKind === 'FREIGHT_FORWARDER'
                              ? (r.payeeName ?? 'Forwarder')
                              : (r.vendorName ?? 'Vendor')}
                    </span>
                ),
            },
            {
                // Its own column, not a mark on the payee name: a partner and a vendor are both
                // just company names, so "Zhechem" under Pay to says nothing about why. As a
                // column it sorts, filters and downloads like any other attribute of the row.
                // Per payment, not per order — the advance on a credit-route order still goes
                // to the vendor, and payeeKind is stamped on each tranche for exactly that.
                key: 'payeeKind',
                header: 'Route',
                width: 96,
                render: (v: PayableItem['payeeKind']) => {
                    const credit = v === 'CREDIT_PARTNER';
                    const freight = v === 'FREIGHT_FORWARDER';
                    return (
                        <span
                            style={{
                                display: 'inline-block',
                                padding: '1px 8px',
                                borderRadius: 999,
                                fontSize: 11,
                                fontWeight: 600,
                                background: credit ? 'rgb(237, 233, 254)' : freight ? 'rgb(224, 242, 254)' : 'rgb(241, 245, 249)',
                                color: credit ? 'rgb(91, 33, 182)' : freight ? 'rgb(3, 105, 161)' : textColors.secondary,
                            }}
                        >
                            {credit ? 'Credit' : freight ? 'Freight' : 'Direct'}
                        </span>
                    );
                },
            },
            {
                key: 'outstanding',
                header: 'Outstanding',
                width: 140,
                align: 'right' as const,
                render: (_: unknown, r: PayableItem) => (
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: textColors.primary,
                        }}
                    >
                        {formatMoney(Math.max(0, r.outstanding), r.currency)}
                    </span>
                ),
            },
            {
                key: 'paidAmount',
                header: 'Paid',
                width: 100,
                align: 'right' as const,
                render: (v: number, r: PayableItem) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {v > 0 ? formatMoney(v, r.currency) : '—'}
                    </span>
                ),
            },
            {
                key: 'departureDate',
                header: 'Departure',
                width: 125,
                render: (v: string | null) => (
                    <span
                        style={{
                            fontSize: 12,
                            color: v ? textColors.primary : textColors.muted,
                        }}
                    >
                        {v ? formatDateDisplay(v) : '—'}
                    </span>
                ),
            },
            {
                key: 'expectedDepartureDate',
                header: 'Exp. departure',
                width: 130,
                render: (v: string | null, r: PayableItem) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {r.departureDate ? '—' : v ? formatDateDisplay(v) : '—'}
                    </span>
                ),
            },
            {
                key: 'dueDate',
                header: 'Due',
                width: 120,
                render: (v: string | null, r: PayableItem) => (
                    <span style={{ fontSize: 12, color: textColors.secondary }}>
                        {v ? formatDateDisplay(v) : '—'}
                        {r.dueDateSource === 'EXPECTED' && (
                            <span
                                style={{ color: textColors.muted }}
                                title="Projected from the expected timeline"
                            >
                                {' '}
                                ~
                            </span>
                        )}
                        {r.dueDateSource === 'OVERRIDE' && (
                            <span title="Pinned manually"> 📌</span>
                        )}
                    </span>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                width: 110,
                render: (v: PaymentTrancheStatus) => <StatusBadge status={v} />,
            },
            {
                key: 'actions',
                header: '',
                width: 100,
                // Same recipe as the Vendor and Due filter pills in the toolbar above
                // (28px / 12px / 500, white on a slate border), so the row action reads
                // as part of this page's own controls rather than a stock button.
                render: (_: unknown, r: PayableItem) => (
                    <button
                        type="button"
                        onClick={() => setMarkPaid(r)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            height: 28,
                            padding: '0 10px',
                            fontSize: 12,
                            fontWeight: 500,
                            color: textColors.primary,
                            backgroundColor: '#ffffff',
                            border: '1px solid rgb(226, 232, 240)',
                            borderRadius: 6,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Mark paid
                    </button>
                ),
            },
        ],
        [],
    );

    return (
        <div className="flex flex-col gap-2 p-6">
            <PageHeader
                icon="💸"
                title="Payments"
                subtitle="What you owe vendors and credit partners, and when — derived live from orders, shipments and recorded payments."
            />

            {dueSoonAlert && (
                <div
                    style={{
                        background: 'rgba(205, 101, 101, 0.08)',
                        border: '1px solid rgba(205, 101, 101, 0.35)',
                        borderRadius: 8,
                        padding: '10px 14px',
                        marginTop: 6,
                    }}
                >
                    <Text size="13px" fw={600} c="rgb(205, 101, 101)">
                        ⚠ {dueSoonAlert.count} payment
                        {dueSoonAlert.count === 1 ? '' : 's'} of {dueSoonAlert.amount}{' '}
                        {dueSoonAlert.count === 1 ? 'is' : 'are'} due to be paid in
                        the next 5 days
                    </Text>
                </div>
            )}

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 12,
                    marginTop: 8,
                }}
            >
                <StatCard
                    label="Overdue"
                    value={view ? usdValue(cardTotals.overdue) : '—'}
                    helper={
                        cardTotals.overdueCount > 0
                            ? `${cardTotals.overdueCount} payment${cardTotals.overdueCount === 1 ? '' : 's'} past due${hasInr(cardTotals.overdue) ? ` · INR @ ₹${INR_PER_USD}/$` : ''}`
                            : 'nothing overdue'
                    }
                    valueColor={
                        cardTotals.overdueCount > 0 ? 'rgb(205, 101, 101)' : undefined
                    }
                    loading={isLoading}
                />
                <StatCard
                    label="Due this week"
                    value={view ? usdValue(cardTotals.dueThisWeek) : '—'}
                    helper={
                        hasInr(cardTotals.dueThisWeek)
                            ? `anchored on real dates · INR @ ₹${INR_PER_USD}/$`
                            : 'anchored on real dates'
                    }
                    loading={isLoading}
                />
                <StatCard
                    label="Next 30 days"
                    value={view ? usdValue(cardTotals.next30) : '—'}
                    helper={
                        hasInr(cardTotals.next30)
                            ? `including projections · INR @ ₹${INR_PER_USD}/$`
                            : 'including projections'
                    }
                    loading={isLoading}
                />
                <StatCard
                    label="Unscheduled"
                    value={view ? usdValue(cardTotals.unscheduled) : '—'}
                    helper={
                        cardTotals.unscheduledCount > 0
                            ? `${cardTotals.unscheduledCount} payment${cardTotals.unscheduledCount === 1 ? '' : 's'} without a reliable date${hasInr(cardTotals.unscheduled) ? ` · INR @ ₹${INR_PER_USD}/$` : ''}`
                            : 'all payments have dates'
                    }
                    valueColor={
                        cardTotals.unscheduledCount > 0
                            ? 'rgb(124, 95, 240)'
                            : undefined
                    }
                    loading={isLoading}
                />
            </div>

            {view && (
                <>
                    <DataSheet<PayableItem>
                        data={filteredItems}
                        columns={columns}
                        isLoading={isLoading}
                        getRowKey={(r) => r.trancheId}
                        title="Open payments"
                        subtitle={`${filteredItems.length}${hasFilters ? ` of ${openItems.length}` : ''} open · ${cardTotals.overdueCount} overdue · ${cardTotals.unscheduledCount} unscheduled`}
                        enableSearch
                        searchPlaceholder="Search by order, vendor or payment..."
                        filters={[vendorFilterConfig]}
                        toolbarControls={
                            <Group gap={8} wrap="nowrap">
                                <span style={{ position: 'relative' }}>
                                    {/* Styled to match the EntityFilterDropdown
                                        trigger pill exactly (28px / 12px / 500). */}
                                    <button
                                        type="button"
                                        onClick={() => setShowDueCalendar((v) => !v)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            height: 28,
                                            padding: '0 8px',
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color:
                                                dueFrom || dueTo
                                                    ? 'rgb(1, 119, 204)'
                                                    : textColors.primary,
                                            backgroundColor:
                                                dueFrom || dueTo
                                                    ? 'rgba(1, 149, 255, 0.06)'
                                                    : '#ffffff',
                                            border: `1px solid ${
                                                showDueCalendar
                                                    ? 'rgb(1, 149, 255)'
                                                    : dueFrom || dueTo
                                                      ? 'rgba(1, 149, 255, 0.3)'
                                                      : 'rgb(226, 232, 240)'
                                            }`,
                                            borderRadius: 6,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        {dueFrom || dueTo
                                            ? `Due: ${dueFrom ? formatDateDisplay(dueFrom) : '…'} → ${dueTo ? formatDateDisplay(dueTo) : '…'}`
                                            : 'Due: Any'}
                                    </button>
                                    {showDueCalendar && (
                                        <DatePopover
                                            range={{
                                                startDate: dueFrom || (view?.today ?? ''),
                                                endDate: dueTo || (view?.today ?? ''),
                                            }}
                                            onClose={() => setShowDueCalendar(false)}
                                            onApply={(range: UiDateRange) => {
                                                setDueFrom(range.startDate);
                                                setDueTo(range.endDate);
                                                setShowDueCalendar(false);
                                            }}
                                        />
                                    )}
                                </span>
                                {(dueFrom || dueTo) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setDueFrom('');
                                            setDueTo('');
                                        }}
                                        title="Clear the due-date range"
                                        style={{
                                            height: 28,
                                            padding: '0 6px',
                                            fontSize: 12,
                                            border: 'none',
                                            background: 'transparent',
                                            color: textColors.secondary,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✕
                                    </button>
                                )}
                                <Switch
                                    size="xs"
                                    label="Forecast"
                                    checked={includeForecast}
                                    onChange={(e) =>
                                        setIncludeForecast(e.currentTarget.checked)
                                    }
                                />
                            </Group>
                        }
                        emptyState={
                            hasFilters
                                ? 'No payments match the current filters.'
                                : 'No open payment obligations. Schedules appear here once orders carry payment terms.'
                        }
                        onRowClick={(r) => {
                            if (r.level !== 'MOTHER') setSelectedPoId(r.orderId);
                        }}
                    />
                    <Text size="11px" c={textColors.muted} mt={4}>
                        ~ after a date = projected from the expected timeline (never
                        flagged overdue). Unscheduled = no dispatch date yet, or the
                        projection has passed — set the shipment's dates, or pin a
                        due date on the tranche.
                    </Text>

                    {includeForecast && view.forecast.length > 0 && (
                        <>
                            <SectionTitle
                                title="Forecast — goods still in production"
                                sub="Projected payments for the un-shipped remainder of open orders; they become real as shipments are created"
                            />
                            {view.forecast
                                .filter(
                                    (f) =>
                                        selectedVendors.length === 0 ||
                                        (f.vendorName != null &&
                                            selectedVendors.includes(f.vendorName)),
                                )
                                .map((f) => (
                                    <Group key={f.motherOrderId} gap={10} mb={4}>
                                        <Text size="12px" c={textColors.primary} fw={600}>
                                            {f.orderRef}
                                        </Text>
                                        <Text size="12px" c={textColors.secondary}>
                                            {f.vendorName ?? '—'}
                                        </Text>
                                        <Text size="12px" c={textColors.muted}>
                                            {formatMoney(f.residualValue, f.currency)} of
                                            goods →{' '}
                                            {f.tranches
                                                .map(
                                                    (t) =>
                                                        `${t.label} ${formatMoney(t.amount, f.currency)}`,
                                                )
                                                .join(' · ')}
                                        </Text>
                                    </Group>
                                ))}
                        </>
                    )}
                </>
            )}

            <MarkPaidModal
                target={markPaid ? payableToTarget(markPaid) : null}
                currency={markPaid?.currency ?? 'INR'}
                onClose={() => setMarkPaid(null)}
                onSaved={refresh}
            />

            <ShipmentDetailPanel
                shipment={selectedPoId ? (selectedShipment ?? null) : null}
                onClose={() => setSelectedPoId(null)}
                onChanged={() => {
                    refresh();
                    void queryClient.invalidateQueries(['shipment-detail', selectedPoId]);
                    void queryClient.invalidateQueries(['po-payments', selectedPoId]);
                }}
            />
        </div>
    );
};

export default PaymentsContainer;
